/**
 * TV mode: the app driven by a remote (arrows, OK, Back) inside the Fire TV
 * app in tv-app/, which is a full-screen WebView that adds "WATV/" to its
 * user agent. A browser can try it with ?tv=1 (and turn it off with ?tv=0).
 *
 * - Arrows move focus to the nearest focusable thing in that direction, kept
 *   inside an open dialog (the title modal, the live player) when there is one.
 *   Text fields keep left/right for the caret. The film player keeps all four
 *   arrows (skip, volume), as it does on a keyboard.
 * - OK is Enter, which clicks links and buttons natively. Over a player with
 *   nothing focused it plays/pauses, since a remote has no space bar.
 * - Back (window.__waBack, called by the app) sends Escape first, because the
 *   modal, the live player and every menu already close on it. If nothing
 *   closed, it goes back a page, and on a top-level page it asks the app to exit.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Pages where Back leaves the app rather than the page
const ROOTS = new Set(['/', '/home', '/login', '/signup']);

const DIRS = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };

function detect() {
  try {
    const param = new URLSearchParams(window.location.search).get('tv');
    if (param === '1') localStorage.setItem('wa.tv', '1');
    if (param === '0') localStorage.removeItem('wa.tv');
    if (localStorage.getItem('wa.tv') === '1') return true;
  } catch (err) {
    // storage blocked: the user agent still decides
  }
  return /\bWATV\//.test(navigator.userAgent);
}

export const isTV = detect();

const visible = (el) => {
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const style = getComputedStyle(el);
  return style.visibility !== 'hidden' && style.opacity !== '0' && !el.closest('[inert], [aria-hidden="true"]');
};

/** The part of the page arrows may move around in: the topmost dialog, else everything. */
function scope() {
  const dialogs = document.querySelectorAll('[role="dialog"]');
  return dialogs.length ? dialogs[dialogs.length - 1] : document.body;
}

const playerOpen = () => Boolean(document.querySelector('.player, .lp-stage'));

function candidates(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(visible);
}

/**
 * The nearest candidate in a direction. Distance along the direction counts
 * once, sideways distance counts three times, so moving down from a card picks
 * the card under it rather than one further along a closer row. Up and down
 * only consider what is on screen horizontally, so they never jump into the
 * hidden end of a scrolled rail.
 */
function nearest(from, dir, list) {
  const a = from.getBoundingClientRect();
  const ax = a.left + a.width / 2;
  const ay = a.top + a.height / 2;
  let best = null;
  let bestScore = Infinity;
  for (const el of list) {
    if (el === from || from.contains(el) || el.contains(from)) continue;
    const b = el.getBoundingClientRect();
    const bx = b.left + b.width / 2;
    const by = b.top + b.height / 2;
    let along;
    let side;
    if (dir === 'right') { along = b.left - a.right; side = Math.abs(by - ay); if (bx <= ax + 1) continue; }
    else if (dir === 'left') { along = a.left - b.right; side = Math.abs(by - ay); if (bx >= ax - 1) continue; }
    else if (dir === 'down') { along = b.top - a.bottom; side = Math.abs(bx - ax); if (by <= ay + 1) continue; }
    else { along = a.top - b.bottom; side = Math.abs(bx - ax); if (by >= ay - 1) continue; }
    if ((dir === 'up' || dir === 'down') && (b.right < 0 || b.left > window.innerWidth)) continue;
    const score = Math.max(along, -8) + side * 3;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

/** Where focus starts when nothing has it: the first thing on screen in the main content. */
function first(list) {
  const inView = list.filter((el) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
  });
  const main = inView.filter((el) => !el.closest('header'));
  return main[0] || inView[0] || list[0] || null;
}

function move(el, dir) {
  el.focus({ preventScroll: true });
  el.scrollIntoView({
    block: dir === 'up' || dir === 'down' ? 'center' : 'nearest',
    inline: 'nearest',
    behavior: 'smooth'
  });
}

function onKey(event) {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
  const active = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;

  if (event.key === 'Enter' && playerOpen() && (!active || active.tagName === 'VIDEO')) {
    event.preventDefault();
    window.dispatchEvent(new KeyboardEvent('keydown', { key: ' ', code: 'Space', keyCode: 32, bubbles: true, cancelable: true }));
    return;
  }

  const dir = DIRS[event.key];
  if (!dir) return;
  // The film player's arrows skip and set volume, as on a keyboard
  if (document.querySelector('.player') && !document.querySelector('[role="dialog"]')) return;
  if (active) {
    const tag = active.tagName;
    const textual = tag === 'TEXTAREA' || active.isContentEditable ||
      (tag === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'range'].includes(active.type));
    if (textual && (dir === 'left' || dir === 'right')) return;
    if (tag === 'SELECT' || (tag === 'INPUT' && active.type === 'range')) return;
  }

  const root = scope();
  const list = candidates(root);
  const next = active && root.contains(active) ? nearest(active, dir, list) : first(list);
  if (!next) return;
  event.preventDefault();
  move(next, dir);
}

/** A cheap fingerprint of "what is open": the URL and how many elements exist. */
const signature = () => `${window.location.href}|${document.getElementsByTagName('*').length}`;

function back() {
  const before = signature();
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true, cancelable: true }));
  setTimeout(() => {
    if (signature() !== before) return; // something closed
    if (ROOTS.has(window.location.pathname)) {
      if (window.WATV && window.WATV.exit) window.WATV.exit();
      return;
    }
    // react-router keeps its own index in history.state: 0 means this page was
    // opened directly, so there is nothing of ours to go back to
    if (window.history.state && window.history.state.idx > 0) window.history.back();
    else window.location.assign('/home');
  }, 120);
  return true;
}

export function startTV() {
  if (!isTV) return;
  document.documentElement.classList.add('tv');
  document.addEventListener('keydown', onKey);
  window.__waBack = back;
}
